import React, { createContext, useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useOktaAuth } from '@okta/okta-react';
import { useIdleTimer } from 'react-idle-timer';
import { useNavigate } from 'react-router-dom';
import { useLocalStorage } from 'src/hooks/useLocalStorage';
import SessionTimeoutDialog from 'src/components/dialogs/SessionTimeoutDialog';
import useUser from 'src/hooks/useUser';
import { sleep } from 'src/utils/helpers/timer';
import config from 'src/config';

const initialState = {
  session: null
};

const SessionContext = createContext({
  ...initialState,
  loadUser: () => Promise.resolve()
});

const {
  storageKeys: { user: userKey }
} = config;

const cleanStr = (str) => str.replace(/[^a-zA-Z ]/g, '').trim();

export const SessionProvider = (props) => {
  const { children } = props;
  const [session, setSession] = useState(initialState.session);
  const [isIdle, setIdle] = useState(false);
  const { oktaAuth, authState } = useOktaAuth();
  const { token, tokenManager } = oktaAuth;
  const { fetchUserByEmail, addUser } = useUser();
  const navigate = useNavigate();
  const {
    value: userValue,
    setValue: setUserValue,
    removeValue: removeUserValue
  } = useLocalStorage(userKey, null);

  const idleTimer = useIdleTimer({
    timeout: 1800000, // 30 mins
    onIdle: () => {
      setIdle(true);
    },
    onActive: () => {
      setIdle(false);
    },
    startOnMount: false,
    startManually: true,
    stopOnIdle: true,
    debounce: 500,
    crossTab: {
      emitOnAllTabs: true
    }
  });

  const emitRemove = useCallback(() => removeUserValue, [
    oktaAuth,
    tokenManager
  ]);

  useEffect(() => {
    const handleRemove = () => {
      emitRemove();
    };
    tokenManager.on('removed', handleRemove);
    return () => {
      tokenManager.off('removed', handleRemove);
    };
  }, []);

  useEffect(() => {
    (async () => {
      if (session && (await oktaAuth.isAuthenticated())) {
        idleTimer.start();
      } else {
        idleTimer.pause();
      }
    })();
  }, [session, oktaAuth]);

  const clearTimer = () => {
    setIdle(false);
    idleTimer.pause();
  };

  const keepAlive = useCallback(async () => {
    try {
      if ((await oktaAuth.isAuthenticated()) && authState.isAuthenticated) {
        idleTimer.reset();
      } else {
        token.getWithRedirect();
      }
    } catch (error) {
      token.getWithRedirect();
    }
  }, [oktaAuth]);

  const fetchOktaUser = async (counter = 5) => {
    try {
      return await oktaAuth.getUser();
    } catch (error) {
      // 5 times retry and then return null
      if (counter > 0) {
        await sleep(2000);
        return fetchOktaUser(counter - 1);
      }
    }
    return null;
  };

  const logout = async () => {
    removeUserValue();
    setSession(null);
    await oktaAuth.revokeAccessToken();
    navigate('/sso?logout=true');
  };

  const loadSession = useCallback(
    async (force = false) => {
      const oktaUser = await fetchOktaUser();

      const setSessionUser = async () => {
        if (oktaUser) {
          let bsUser;
          try {
            bsUser = await fetchUserByEmail(encodeURIComponent(oktaUser.email));
          } catch (error) {
            bsUser = await addUser({
              ...oktaUser,
              is_new: 1,
              family_name: cleanStr(oktaUser.family_name),
              given_name: cleanStr(oktaUser.given_name),
              name: cleanStr(oktaUser.name)
            });
          }
          const userObject = {
            ...bsUser,
            ...oktaUser
          };

          setUserValue(userObject);
          setSession({
            user: userObject
          });
        } else {
          throw new Error('Unable to fetch Okta user.');
        }
      };

      if (force) {
        await setSessionUser();
      } else {
        // bail if session is already loaded
        if (session) return;

        const {
          accessToken: {
            claims: { uid: oktaUserId }
          }
        } = authState;

        // if other user
        if (userValue?.sub !== oktaUserId) {
          await setSessionUser();
        } else {
          setSession({
            user: {
              ...userValue,
              ...oktaUser
            }
          });
        }
      }
    },
    [authState]
  );

  return (
    <SessionContext.Provider
      value={{
        session,
        isIdle,
        logout,
        loadSession,
        keepAlive,
        clearTimer
      }}
      {...props}
    >
      {children}
      <SessionTimeoutDialog />
    </SessionContext.Provider>
  );
};

SessionProvider.propTypes = {
  children: PropTypes.node.isRequired
};

export default SessionContext;
